'use client'

import { useEffect, useRef, useState } from 'react'
import { useMediaQuery } from '@segway/core'
import { AffirmDataParsed } from '@segway/core/src/businessHooks/useProductAffirmData'
import isUndefined from 'lodash/isUndefined'

type AffirmWidgetProps = {
  totalPrice: number
  affirmData: AffirmDataParsed | null
}

// https://docs.affirm.com/payments/docs/promo-messaging-getting-started
// https://docs.affirm.com/platforms/docs/about-promotional-messaging
// https://docs.affirm.com/payments/docs/partner-test-and-go-live

const AffirmWidget = ({ totalPrice, affirmData }: AffirmWidgetProps) => {
  const [isReady, setIsReady] = useState(false)
  const responsive = useMediaQuery()

  const handleAffirmReady = () => {
    setIsReady(true)
  }

  useEffect(() => {
    if (isUndefined(window) || !affirmData) return

    if (window.affirm) {
      handleAffirmReady()

      return
    }

    window._affirm_config = {
      public_api_key: affirmData.apiKeyPublic,
      script: affirmData.script,
      locale: affirmData.locale,
      country_code: affirmData.countryCode,
    }

    const loadAffirmScript = () => {
      const script = document.createElement('script')

      script.src = affirmData.script
      script.async = true
      script.onload = () => {
        if (window.affirm && !isReady) {
          handleAffirmReady()
        }
      }
      document.body.appendChild(script)
    }

    if (!document.querySelector(`script[src="${affirmData.script}"]`)) {
      loadAffirmScript()
    }

    return () => {
      const script = document.querySelector(`script[src="${affirmData.script}"]`)

      if (script) {
        document.body.removeChild(script)
      }
    }
  }, [affirmData, isReady])

  const widgetRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (window.affirm && widgetRef.current) {
      window.affirm.ui.refresh()
    }
  }, [totalPrice])

  useEffect(() => {
    const style = document.createElement('style')
    style.textContent = `
      .affirm-as-low-as {
        font-family: var(--font-family-mi-sans-regular-330);
        color: #0F0F0F;
        font-size: 14px;
        line-height: 1.4;
      }

      .affirm-as-low-as .affirm-modal-trigger {
        font-family: var(--font-family-mi-sans-demi-bold-450) !important;
        cursor: pointer !important;
        font-size: 14px;
        line-height: 1.4;
        text-decoration: underline !important;
      }

      @media (min-width: 1200px) {
        .affirm-as-low-as {
            font-size: 16px;
        }

        .affirm-as-low-as .affirm-modal-trigger {
          font-size: 16px;
        }
      }
    `
    document.head.appendChild(style)

    return () => {
      document.head.removeChild(style)
    }
  }, [responsive])

  if (!affirmData) return null

  return (
    <div
      ref={widgetRef}
      id={affirmData.element_id || 'als_pdp'}
      className="affirm-as-low-as"
      data-amount={totalPrice * 100}
      data-page-type="product"
      data-apr={affirmData?.apr}
      data-max-order-total={affirmData?.max_order_total}
      data-min-order-total={affirmData?.min_order_total}
      data-promo-id={affirmData.mfp_value || undefined}
      data-affirm-color="blue"
      data-months={affirmData.months}
      data-learnmore-show={affirmData.learn_more === '1' ? 'true' : 'false'}
      data-affirm-logo={affirmData.affirm_assets_url}
    />
  )
}

export default AffirmWidget
